/**
 * Step 1 - Host + Product Selection (data-driven)
 *
 * A single component that replaces StepVehicleProduct, StepPropertyProduct,
 * and StepVesselProduct. Renders the host panel dynamically based on
 * metadata.host_template.attributes.
 *
 * Two-column layout: left 45% (host), right 55% (product).
 * Below both columns: Additional Details panel (full width, animates in).
 * Below that: Continue button (right-aligned, animates in).
 */

import { useState, useMemo, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, Check, Car, Home, Ship, Package } from 'lucide-react'
import { SearchInput } from '@/shared/components/ui/search-input'
import { useVehicleLookup } from '@/admin/hooks/use-lookups'
import { useAddressAutocomplete, useAddressResolve } from '@/admin/hooks/use-lookups'
import type { AddressSuggestion, ResolvedAddress } from '@/admin/hooks/use-lookups'
import type { WizardState } from '@/admin/pages/registrations/new-registration-page'
import type { RegistrationFormMetadata } from '@/admin/types/products'
import type { HostTemplate, HostAttributeMeta } from '@/admin/types/hosts'
import {
  ProductPanel,
  AdditionalDetailCards,
  isWarrantySelectionResolved,
} from './shared'
import { extractApiErrorMessage } from '@/shared/lib/api-error'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { RegistrationPanel, RegistrationStepActions, RegistrationStepGrid } from './layout'
import { MetadataField, normaliseTemplateAttribute } from '@/shared/lib/forms'
import {
  useRegisterValidator,
  useWizardValidation,
} from './wizard-validation'
import { MailingAddressPanel } from './mailing-address-panel'
import {
  missingRequiredHostAttributes,
  requiredHostAttributeErrors,
} from './host-required-fields'
import {
  StructurePickerPanel,
  type StructurePickerActivation,
} from './structure-picker-panel'
import {
  useDataCaptureConfig,
  isVisible,
  isRequired,
} from '@/admin/hooks/use-data-capture-config'
import type { DataCaptureConfig } from '@/admin/hooks/use-registration-settings'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StepHostProductProps {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  next: () => void
  back?: () => void
  continueDisabled?: boolean
  continueHint?: string | null
  showProductPanel?: boolean
  showAdditionalDetails?: boolean
  metadata: RegistrationFormMetadata | null
  /**
   * Extra panel(s) to render alongside this step's own panels in the
   * shared `RegistrationStepGrid`. Used by the page to slot in the
   * shared `RegistrationFieldsPanel` so that all step panels (host,
   * registration fields, etc.) flow through one 2-column grid.
   */
  extraPanels?: React.ReactNode
  /**
   * When true, the inner `ProductPanel` renders a flat product list
   * (no category cards). Forwarded to `ProductPanel`.
   */
  flatProductSelection?: boolean
  dataCaptureConfig?: DataCaptureConfig
  earlyCaptureFields?: string[]
  /**
   * Bubbles the structure picker's activation up to the page so it can
   * drop picker-owned registration fields (e.g. `other_structure_type`)
   * from this step's validation - those values flow via the picker into
   * `hostAttributeValues`, not `registrationFieldValues`.
   */
  onStructurePickerActivation?: (activation: StructurePickerActivation) => void
}

// ---------------------------------------------------------------------------
// Icon resolver
// ---------------------------------------------------------------------------

function HostIcon({ icon, size, className }: { icon: string; size: number; className?: string }) {
  switch (icon) {
    case 'car':
      return <Car size={size} className={className} />
    case 'home':
    case 'house':
      return <Home size={size} className={className} />
    case 'ship':
    case 'boat':
    case 'anchor':
      return <Ship size={size} className={className} />
    default:
      return <Package size={size} className={className} />
  }
}

// ---------------------------------------------------------------------------
// DVLA vehicle lookup helpers
// ---------------------------------------------------------------------------

/** Detect product category from DVLA fuel_type / make for auto-selecting category tab. */
function detectCategory(data: { fuel_type?: string; make?: string } | null): string | null {
  if (!data) return null
  const fuel = (data.fuel_type ?? '').toLowerCase()
  const make = (data.make ?? '').toLowerCase()

  const motoBrands = [
    'harley', 'ducati', 'yamaha', 'kawasaki', 'honda', 'suzuki',
    'triumph', 'ktm', 'bmw motorrad', 'aprilia', 'indian', 'royal enfield',
  ]
  if (motoBrands.some((b) => make.includes(b))) return 'motorcycle'

  const caravanBrands = [
    'swift', 'bailey', 'elddis', 'lunar', 'coachman', 'adria',
    'buccaneer', 'autotrail', 'auto-trail', 'bessacarr', 'compass',
  ]
  if (caravanBrands.some((b) => make.includes(b))) return 'caravan-motorhome'

  if (
    fuel.includes('petrol') || fuel.includes('diesel') ||
    fuel.includes('electric') || fuel.includes('hybrid')
  ) return 'motor-vehicle'

  return null
}

// ---------------------------------------------------------------------------
// DVLA Lookup Section
// ---------------------------------------------------------------------------

function DVLALookupSection({
  attributes,
  hostValues,
  onValuesChange,
  onCategoryDetected,
  attempted = false,
  attributeErrors,
}: {
  attributes: HostAttributeMeta[]
  hostValues: Record<string, unknown>
  onValuesChange: (values: Record<string, unknown>) => void
  onCategoryDetected: (slug: string | null) => void
  attempted?: boolean
  attributeErrors?: Record<string, string>
}) {
  const lookupAttr = attributes.find((a) => a.lookup_provider === 'dvla')
  const externalPlate = String(hostValues[lookupAttr?.code_name ?? ''] ?? '')
  const [draftPlate, setDraftPlate] = useState('')
  const plate = draftPlate || externalPlate
  const lookup = useVehicleLookup()

  const handlePlateChange = (raw: string) => {
    const cleaned = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 8)
    setDraftPlate(cleaned)
    if (lookupAttr) {
      onValuesChange({ ...hostValues, [lookupAttr.code_name]: cleaned })
    }
  }

  const handleLookup = () => {
    if (!plate) return
    lookup.mutate(plate, {
      onSuccess: (data) => {
        // Map the lookup response onto prepopulated attributes. Coverage varies
        // by provider - DVLA returns no model and no VIN - so a blank value
        // means "this provider does not know", never "clear what is there".
        const lookupValues: Record<string, string> = {
          make: data.make,
          model: data.model,
          variant: data.variant ?? '',
          vin: data.vin ?? '',
          colour: data.colour,
          year: data.year,
          fuel_type: data.fuel_type,
          engine_type: data.engine_type,
          tax_status: data.tax_status,
          mot_status: data.mot_status,
          month_of_first_registration: data.month_of_first_registration,
          registration: data.registration,
        }
        const newValues = { ...hostValues }
        for (const attr of attributes) {
          if (attr.is_prepopulated) {
            // Try exact code_name match first, then maps_to_column
            const val = lookupValues[attr.code_name] ?? lookupValues[attr.maps_to_column]
            // Only write a real value: overwriting with "" wiped the model and
            // VIN a dealer had already typed whenever the provider omitted them.
            if (val) {
              newValues[attr.code_name] = val
            }
          }
        }
        if (lookupAttr) {
          newValues[lookupAttr.code_name] = plate
        }
        onValuesChange(newValues)
        onCategoryDetected(detectCategory(data))
      },
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleLookup()
    }
  }

  // Separate prepopulated (auto-filled from lookup) from the lookup field itself
  const prepopulatedAttrs = attributes.filter((a) => a.is_prepopulated && a.code_name !== lookupAttr?.code_name)

  const isLookupRequired = lookupAttr?.required ?? false
  const showLookupError = attempted && isLookupRequired && !plate

  return (
    <>
      {/* Registration + Look Up row */}
      {isLookupRequired && (
        <Label className="mb-1.5 block clara-label font-normal">
          <span className="inline-flex items-center gap-[2px]">
            <span>{lookupAttr?.display_name || 'Registration Number'}</span>
            <span aria-hidden="true" className="mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">*</span>
          </span>
        </Label>
      )}
      <div className="flex gap-2">
        <Input
          type="text"
          value={plate}
          onChange={(e) => handlePlateChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={lookupAttr?.hint || 'e.g. AB21 XYZ'}
          maxLength={8}
          autoComplete="off"
          data-lpignore="true"
          data-form-type="other"
          aria-invalid={showLookupError || undefined}
          className={cn(
            "h-9 flex-1 rounded-[4px] border-[0.5px] bg-[var(--bg)] px-3 font-sans text-[13px] uppercase tracking-[0.1em] text-[var(--ink)] outline-none transition-[border-color] duration-150 focus:border-[var(--green)]",
            showLookupError ? "border-[var(--status-error)]" : "border-[var(--rule)]",
          )}
        />
        <Button
          type="button"
          onClick={handleLookup}
          disabled={!plate || lookup.isPending}
          className="inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-[4px] border-none bg-[var(--green)] px-3 text-[11px] font-light uppercase tracking-[0.1em] text-white transition-opacity duration-150"
        >
          {lookup.isPending ? (
            <span
              className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-[1.5px] border-[rgba(255,255,255,0.3)] border-t-white"
            />
          ) : null}
          {lookup.isPending ? 'Looking up\u2026' : 'Look Up'}
        </Button>
      </div>
      {/* Validation error */}
      {showLookupError && (
        <p className="mt-1 text-[11px] text-[var(--status-error)]">
          Please enter {lookupAttr?.display_name?.toLowerCase() || 'registration number'}
        </p>
      )}
      {/* Lookup error. Prefer the server's message: a 404 genuinely means the
          registration was not found, but a 502/503 means our provider is
          unavailable or unconfigured, and telling the dealer to check a correct
          registration is what made SX-217 look like a data problem. */}
      {lookup.isError && (
        <p className="mt-2 text-[12px] text-[var(--ink-3)]">
          {extractApiErrorMessage(lookup.error) ||
            'Vehicle not found. Enter details manually below.'}
        </p>
      )}
      {/* Prepopulated fields - always editable, pre-filled after lookup */}
      {prepopulatedAttrs.length > 0 && (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {prepopulatedAttrs.map((attr) => (
            <MetadataField
              key={attr.uuid}
              field={normaliseTemplateAttribute(attr)}
              value={hostValues[attr.code_name] ?? ''}
              onChange={(v) => onValuesChange({ ...hostValues, [attr.code_name]: v })}
              labelVariant="compact"
              error={attributeErrors?.[attr.code_name]}
            />
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Address Lookup Section
// ---------------------------------------------------------------------------

/**
 * Address lookup section - a concrete implementation of the generic
 * "lookup-driven host attributes" pattern.
 *
 * Draft / commit boundary
 * -----------------------
 * The section keeps an internal draft of the values the user is
 * entering (search term, manual entry fields, in-flight lookup result).
 * Nothing is written to ``hostAttributeValues`` while the user is
 * still entering or revising the data. ``onValuesChange`` is only
 * called at two commit points:
 *   1. The lookup API returns successfully and the result has been
 *      formatted into a host attribute payload.
 *   2. The user clicks **Confirm** in manual entry mode and every
 *      attribute the template declares as ``required`` is present
 *      in the draft.
 *
 * Editing the committed value (Edit button, Search instead, or
 * starting a new search after a commit) clears the previously
 * committed values from ``hostAttributeValues``. This means the
 * rule engine - which reads ``hostAttributeValues`` - only ever
 * sees committed input, so rules such as the duplicate-property
 * warning fire exactly once per commit and re-fire after edit +
 * re-confirm. There is no "confirmed" flag in wizard state and no
 * special-cased rule context filtering.
 *
 * The ``addressResponseKeyMap`` below is the only place that knows
 * about the shape of the address API response. Everything else is
 * fully driven by the template's attributes.
 */
function AddressLookupSection({
  attributes,
  hostValues,
  onValuesChange,
}: {
  attributes: HostAttributeMeta[]
  hostValues: Record<string, unknown>
  onValuesChange: (values: Record<string, unknown>) => void
}) {
  const addressResponseKeyMap: Record<string, keyof ResolvedAddress> = {
    line_1: 'line_1',
    address_line_1: 'line_1',
    line_2: 'line_2',
    address_line_2: 'line_2',
    town_or_city: 'town_or_city',
    city: 'town_or_city',
    town: 'town_or_city',
    address_town_or_city: 'town_or_city',
    county: 'county',
    address_county: 'county',
    postcode: 'postcode',
    post_code: 'postcode',
    address_postcode: 'postcode',
    country: 'country',
    address_country: 'country',
  }

  const orderedAttrs = useMemo(
    () => [...attributes].sort((a, b) => a.order - b.order),
    [attributes],
  )
  const compositeAddressAttr = orderedAttrs.find((a) => a.field_type === 'ADDRESS')
  const manualAttrs = compositeAddressAttr ? [compositeAddressAttr] : orderedAttrs
  const lookupAttr = orderedAttrs.find((a) => Boolean(a.lookup_provider))

  const addressFromResolved = (resolved: ResolvedAddress): Record<string, string> => ({
    addressLine1: resolved.line_1 ?? '',
    addressLine2: resolved.line_2 ?? '',
    locality: resolved.town_or_city ?? '',
    region: resolved.county ?? '',
    postalCode: resolved.postcode ?? '',
    countryCode: resolved.country ?? '',
  })

  const addressToResolvedParts = (
    value: unknown,
  ): Partial<Record<keyof ResolvedAddress, string>> => {
    if (!value || typeof value !== 'object') return {}
    const record = value as Record<string, unknown>
    return {
      line_1: String(record.addressLine1 ?? ''),
      line_2: String(record.addressLine2 ?? ''),
      town_or_city: String(record.locality ?? ''),
      county: String(record.region ?? ''),
      postcode: String(record.postalCode ?? ''),
      country: String(record.countryCode ?? ''),
    }
  }

  const syncCompositeAddressParts = (
    value: unknown,
    base: Record<string, unknown>,
  ): Record<string, unknown> => {
    const parts = addressToResolvedParts(value)
    const next = { ...base }
    for (const attr of orderedAttrs) {
      if (!attr.maps_to_column) continue
      const key = addressResponseKeyMap[attr.maps_to_column]
      const part = key ? parts[key] : undefined
      if (part) next[attr.code_name] = part
    }
    return next
  }

  const resolvedKeyFor = (
    attr: HostAttributeMeta,
  ): keyof ResolvedAddress | undefined =>
    addressResponseKeyMap[attr.code_name] ?? addressResponseKeyMap[attr.maps_to_column]

  const buildPayloadFromResolved = (resolved: ResolvedAddress): Record<string, unknown> => {
    const payload: Record<string, unknown> = {}
    if (compositeAddressAttr) {
      payload[compositeAddressAttr.code_name] = addressFromResolved(resolved)
    }
    for (const attr of orderedAttrs) {
      const key = resolvedKeyFor(attr)
      const v = key ? resolved[key] : undefined
      if (v !== undefined && v !== null && v !== '') {
        payload[attr.code_name] = v
      }
    }
    return payload
  }

  // True when this section's attributes are present in the committed
  // (parent-owned) hostValues. Drives the confirmed view on initial
  // mount when the user navigates back to this step.
  const hasCommittedValues = orderedAttrs.some(
    (attr) => {
      const value = hostValues[attr.code_name]
      if (value && typeof value === 'object') {
        return Object.values(value).some((part) => !!String(part ?? '').trim())
      }
      return value !== undefined && value !== null && value !== ''
    },
  )

  const [confirmed, setConfirmed] = useState(hasCommittedValues)
  const [searchTerm, setSearchTerm] = useState('')
  const { data: suggestions } = useAddressAutocomplete(searchTerm)
  const resolveAddress = useAddressResolve()
  const [manualEntry, setManualEntry] = useState(false)
  // Draft values for manual entry. Never read by anything outside
  // this component until the user clicks Confirm.
  const [draftValues, setDraftValues] = useState<Record<string, unknown>>(() =>
    hasCommittedValues
      ? Object.fromEntries(orderedAttrs.map((a) => [a.code_name, hostValues[a.code_name]]))
      : {},
  )

  const writeCommitted = (payload: Record<string, unknown>) => {
    const next = { ...hostValues }
    for (const attr of orderedAttrs) {
      delete next[attr.code_name]
    }
    Object.assign(next, payload)
    onValuesChange(next)
  }

  const clearCommitted = () => {
    const next = { ...hostValues }
    let changed = false
    for (const attr of orderedAttrs) {
      if (attr.code_name in next) {
        delete next[attr.code_name]
        changed = true
      }
    }
    if (changed) onValuesChange(next)
  }

  const handleSelectSuggestion = (suggestion: AddressSuggestion) => {
    resolveAddress.mutate(
      { id: suggestion.id, term: searchTerm },
      {
        onSuccess: (resolved: ResolvedAddress) => {
          const payload = buildPayloadFromResolved(resolved)
          setDraftValues(payload)
          writeCommitted(payload)
          setConfirmed(true)
          setSearchTerm('')
        },
      },
    )
  }

  const handleEdit = () => {
    setConfirmed(false)
    setManualEntry(false)
    setDraftValues({})
    setSearchTerm('')
    clearCommitted()
  }

  // Generic gate: every required template attribute must have a value
  // in the draft before manual entry can be committed.
  const requiredAttrsMissingInDraft = orderedAttrs.some(
    (attr) => {
      if (!attr.required) return false
      const value = draftValues[attr.code_name]
      if (value && typeof value === 'object') {
        return !Object.values(value).some((part) => !!String(part ?? '').trim())
      }
      return value === undefined || value === null || value === ''
    },
  )

  const handleManualConfirm = () => {
    if (requiredAttrsMissingInDraft) return
    writeCommitted(draftValues)
    setConfirmed(true)
  }

  return (
    <>
      {confirmed ? (
        <div className="relative">
          <Button
            variant="link"
            type="button"
            onClick={handleEdit}
            className="absolute right-0 top-0 h-auto cursor-pointer p-0 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)] transition-opacity duration-150 hover:opacity-60"
          >
            Edit
          </Button>

          <div className="mb-3 flex items-start gap-2">
            <MapPin size={14} className="mt-0.5 shrink-0 text-[var(--green)]" />
            <div>
              {(compositeAddressAttr ? [compositeAddressAttr] : orderedAttrs).map((attr) => {
                const raw = hostValues[attr.code_name]
                const val =
                  raw && typeof raw === 'object'
                    ? [
                        (raw as Record<string, unknown>).addressLine1,
                        (raw as Record<string, unknown>).addressLine2,
                        (raw as Record<string, unknown>).locality,
                        (raw as Record<string, unknown>).region,
                        (raw as Record<string, unknown>).postalCode,
                        (raw as Record<string, unknown>).countryCode,
                      ]
                        .filter(Boolean)
                        .join(', ')
                    : String(raw ?? '')
                if (!val.trim()) return null
                return (
                  <p
                    key={attr.uuid}
                    className="text-[13px] leading-6 text-[var(--ink-2)]"
                  >
                    {val}
                  </p>
                )
              })}
            </div>
          </div>
        </div>
      ) : manualEntry ? (
        <div className="flex flex-col gap-3">
          {manualAttrs.map((attr) => (
            <MetadataField
              key={attr.uuid}
              field={normaliseTemplateAttribute(attr)}
              value={draftValues[attr.code_name] ?? ''}
              onChange={(v) => {
                setDraftValues((prev) => {
                  const next = { ...prev, [attr.code_name]: v }
                  return attr.field_type === 'ADDRESS'
                    ? syncCompositeAddressParts(v, next)
                    : next
                })
              }}
              labelVariant="compact"
            />
          ))}
          <div className="mt-1 flex items-center justify-between">
            <Button
              variant="link"
              type="button"
              onClick={() => {
                setManualEntry(false)
                setDraftValues({})
              }}
              className="h-auto cursor-pointer p-0 text-[10px] font-light uppercase tracking-[0.12em] text-[var(--ink-3)] transition-colors duration-150 hover:text-[var(--ink)]"
            >
              &larr; Search instead
            </Button>
            <Button
              type="button"
              onClick={handleManualConfirm}
              disabled={requiredAttrsMissingInDraft}
              className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-[4px] border-none bg-[var(--green)] px-3 text-[10px] font-light uppercase tracking-[0.1em] text-white transition-opacity duration-150 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Check size={12} />
              Confirm
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={lookupAttr?.hint || 'Search by postcode or address...'}
            className="h-9 text-[13px] focus:border-[var(--green)]"
          />
          {resolveAddress.isPending && (
            <div className="py-4 flex justify-center">
              <span
                className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[var(--rule)] border-t-[var(--green)]"
              />
            </div>
          )}
          {suggestions && suggestions.length > 0 && (
            <div className="clara-scroll mt-2 max-h-[240px] overflow-y-auto border-t-[0.5px] border-t-[var(--rule)] pt-1">
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleSelectSuggestion(s)}
                  className="flex w-full cursor-pointer items-center gap-2 border-none border-b-[0.5px] border-b-[var(--rule)] bg-transparent px-2 py-2.5 text-left text-[12px] text-[var(--ink)] transition-colors duration-150 hover:bg-[var(--bg)]"
                >
                  <MapPin size={12} className="shrink-0 text-[var(--ink-3)]" />
                  {s.address}
                </button>
              ))}
            </div>
          )}
          {searchTerm.length >= 3 && suggestions && suggestions.length === 0 && (
            <p className="mt-2 text-center text-[11px] text-[var(--ink-3)]">
              No addresses found
            </p>
          )}
          <Button
            variant="link"
            type="button"
            onClick={() => {
              setManualEntry(true)
              setDraftValues({})
            }}
            className="mt-1.5 block h-auto cursor-pointer p-0 font-display text-[11px] font-light text-[var(--ink-3)] transition-colors duration-150 hover:text-[var(--ink)]"
          >
            Enter address manually
          </Button>
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Standard Fields Section (no lookup)
// ---------------------------------------------------------------------------

function StandardFieldsSection({
  attributes,
  hostValues,
  onValuesChange,
  attributeErrors,
}: {
  attributes: HostAttributeMeta[]
  hostValues: Record<string, unknown>
  onValuesChange: (values: Record<string, unknown>) => void
  attributeErrors?: Record<string, string>
}) {
  const normalisedAttrs = useMemo(
    () => attributes.map(normaliseTemplateAttribute),
    [attributes],
  )

  return (
    <div className="flex flex-col gap-3">
      {normalisedAttrs.map((field) => (
        <MetadataField
          key={field.code_name}
          field={field}
          value={hostValues[field.code_name] ?? ''}
          onChange={(v) => onValuesChange({ ...hostValues, [field.code_name]: v })}
          labelVariant="compact"
          error={attributeErrors?.[field.code_name]}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Host Panel (LEFT column)
// ---------------------------------------------------------------------------

function HostPanel({
  hostTemplate,
  hostRequirement,
  hostValues,
  isInstalled,
  onValuesChange,
  onIsInstalledChange,
  onCategoryDetected,
  attempted = false,
  excludeCodeNames,
  attributeErrors,
}: {
  hostTemplate: HostTemplate
  hostRequirement: string
  hostValues: Record<string, unknown>
  isInstalled: boolean | null
  onValuesChange: (values: Record<string, unknown>) => void
  onIsInstalledChange: (v: boolean) => void
  onCategoryDetected: (slug: string | null) => void
  attempted?: boolean
  /**
   * Host attribute code_names to hide because the structure picker owns
   * that decision (e.g. `structure_type`, `other_structure_type`).
   */
  excludeCodeNames?: Set<string>
  /** Per-field required errors, keyed by code_name, once Continue was tried. */
  attributeErrors?: Record<string, string>
}) {
  const attrs = useMemo(
    () =>
      [...hostTemplate.attributes]
        .filter((a) => !(excludeCodeNames?.has(a.code_name) ?? false))
        .sort((a, b) => a.order - b.order),
    [hostTemplate.attributes, excludeCodeNames],
  )

  const lookupProvider = attrs.find((a) => !!a.lookup_provider)?.lookup_provider

  // For OPTIONAL hosts, when not installed, collapse the panel
  const isOptional = hostRequirement === 'OPTIONAL'
  const showFields = !isOptional || isInstalled !== false

  // Separate attributes into lookup-driven vs standard
  const lookupAttrs = attrs.filter(
    (a) => a.lookup_provider || a.is_prepopulated,
  )
  const standardAttrs = attrs.filter(
    (a) => !a.lookup_provider && !a.is_prepopulated,
  )

  return (
    <RegistrationPanel
      title={hostTemplate.name}
      icon={
        <HostIcon
          icon={hostTemplate.icon}
          size={14}
          className="text-[var(--ink-3)]"
        />
      }
      className={cn(showFields && 'min-h-[280px]')}
    >
      {/* OPTIONAL toggle */}
      {isOptional && (
        <div>
          <button
            type="button"
            onClick={() => onIsInstalledChange(isInstalled === false ? true : false)}
            className="flex h-9 w-full cursor-pointer items-center gap-3 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 transition-[border-color] duration-150"
          >
            <span className={cn('relative inline-block h-[18px] w-8 shrink-0 rounded-full transition-colors duration-150', isInstalled !== false ? 'bg-[var(--green)]' : 'bg-[var(--rule-strong)]')}>
              <span className={cn('absolute top-[2px] h-[14px] w-[14px] rounded-full bg-[var(--bg)] transition-[left] duration-150', isInstalled !== false ? 'left-4' : 'left-[2px]')} />
            </span>
            <span className="text-[13px] text-[var(--ink)]">
              Is this product installed in a {hostTemplate.name.toLowerCase()}?
            </span>
          </button>
        </div>
      )}
      {/* Host fields */}
      {showFields && (
        <>
          {lookupProvider === 'dvla' && lookupAttrs.length > 0 && (
            <DVLALookupSection
              attributes={lookupAttrs}
              hostValues={hostValues}
              onValuesChange={onValuesChange}
              onCategoryDetected={onCategoryDetected}
              attempted={attempted}
              attributeErrors={attributeErrors}
            />
          )}

          {lookupProvider === 'address' && lookupAttrs.length > 0 && (
            <AddressLookupSection
              attributes={lookupAttrs}
              hostValues={hostValues}
              onValuesChange={onValuesChange}
            />
          )}

          {!lookupProvider && lookupAttrs.length > 0 && (
            <StandardFieldsSection
              attributes={lookupAttrs}
              hostValues={hostValues}
              onValuesChange={onValuesChange}
              attributeErrors={attributeErrors}
            />
          )}

          {standardAttrs.length > 0 && (
            <div className={cn(lookupAttrs.length > 0 && 'mt-3')}>
              <StandardFieldsSection
                attributes={standardAttrs}
                hostValues={hostValues}
                onValuesChange={onValuesChange}
                attributeErrors={attributeErrors}
              />
            </div>
          )}
        </>
      )}
    </RegistrationPanel>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StepHostProduct({
  state,
  update,
  next,
  back,
  // Legacy gating prop retained for prop-shape compatibility; the
  // Continue button is always enabled now.
  // Legacy gating prop - Continue is always enabled now and validation
  // happens via the wizard validation context. Intentionally not
  // destructured here.
  continueHint,
  showProductPanel = true,
  showAdditionalDetails = true,
  metadata,
  extraPanels,
  flatProductSelection = false,
  dataCaptureConfig,
  earlyCaptureFields = [],
  onStructurePickerActivation,
}: StepHostProductProps) {
  const { dcc, fieldLabel } = useDataCaptureConfig(dataCaptureConfig)
  const hostTemplate = metadata?.host_template
  const hostRequirement = metadata?.host_requirement ?? 'REQUIRED'
  const mailingVisible = earlyCaptureFields.includes('consumer_address') && isVisible(dcc?.consumer_address)
  const mailingRequired = mailingVisible && isRequired(dcc?.consumer_address)
  const mailingLabel = fieldLabel('consumer_address', 'Mailing address')

  const [detectedCategory, setDetectedCategory] = useState<string | null>(null)

  const hostValues = useMemo(() => state.hostAttributeValues ?? {}, [state.hostAttributeValues])
  const isInstalled = state.isInstalled

  // When the structure picker is in control of the structure decision
  // (existing property with at least one structure), the host panel must
  // hide and stop requiring the standalone structure fields it owns
  // (structure_type, other_structure_type, ...). The picker writes their
  // values into hostValues, so the standalone dropdowns are redundant.
  const [structurePicker, setStructurePicker] =
    useState<StructurePickerActivation>({ active: false, ownedCodeNames: [] })
  const handleStructurePickerActivation = useCallback(
    (activation: StructurePickerActivation) => {
      setStructurePicker((prev) =>
        prev.active === activation.active &&
        prev.ownedCodeNames.join(',') === activation.ownedCodeNames.join(',')
          ? prev
          : activation,
      )
      // Bubble up so the page can drop picker-owned registration fields
      // from this step's required-field validation.
      onStructurePickerActivation?.(activation)
    },
    [onStructurePickerActivation],
  )
  const structurePickerOwnedCodes = useMemo(
    () =>
      structurePicker.active
        ? new Set(structurePicker.ownedCodeNames)
        : new Set<string>(),
    [structurePicker],
  )

  // Migrate legacy vehicle state into hostAttributeValues when switching
  // from StepVehicleProduct to StepHostProduct after product selection.
  // The DVLA lookup stores data in state.make/model/colour etc., but
  // StepHostProduct reads from state.hostAttributeValues.
  useEffect(() => {
    if (!hostTemplate) return
    // Only migrate if hostAttributeValues is empty and legacy data exists
    const hasLegacy = state.make || state.model || state.colour || state.regPlate || state.vin
    const hasHostValues = Object.values(hostValues).some((v) => v !== undefined && v !== null && v !== '')
    if (!hasLegacy || hasHostValues) return

    const legacyMap: Record<string, string> = {}
    if (state.make) legacyMap.make = state.make
    if (state.model) legacyMap.model = state.model
    if (state.colour) legacyMap.colour = state.colour
    if (state.vin) legacyMap.vin = state.vin

    const newValues: Record<string, unknown> = {}
    for (const attr of hostTemplate.attributes) {
      // DVLA lookup field gets the registration plate
      if (attr.lookup_provider === 'dvla' && state.regPlate) {
        newValues[attr.code_name] = state.regPlate
        continue
      }
      const val = legacyMap[attr.code_name] ?? legacyMap[attr.maps_to_column] ?? undefined
      if (val !== undefined) {
        newValues[attr.code_name] = val
      }
    }
    if (Object.keys(newValues).length > 0) {
      update({ hostAttributeValues: newValues })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostTemplate])

  const handleHostValuesChange = (values: Record<string, unknown>) => {
    update({ hostAttributeValues: values })
  }

  const handleIsInstalledChange = (v: boolean) => {
    update({ isInstalled: v })
  }

  // Warranty resolution gate - product + warranty must be resolved before vehicle shows
  const warrantyTemplates = metadata?.warranty_templates ?? []
  const warrantyResolved = isWarrantySelectionResolved({
    warrantyTemplates,
    selectedClassUuid: state.warrantyTemplateUuid,
    selectedOptionUuid: state.warrantyTemplateOptionUuid,
  })

  // Progressive disclosure gates
  const productPrereqsResolved = !!state.selectedProduct && (!showProductPanel || warrantyResolved)
  const showVehicle = productPrereqsResolved
  const showDetails = showAdditionalDetails && showVehicle

  // Required host attributes still empty, unless the host is OPTIONAL and not
  // installed. Structure-picker-owned fields are satisfied by the picker
  // selection (written into hostValues), not the standalone input.
  const missingHostAttrs = useMemo(() => {
    if (!hostTemplate) return []
    if (hostRequirement === 'OPTIONAL' && isInstalled === false) return []
    return missingRequiredHostAttributes(
      hostTemplate.attributes,
      hostValues,
      structurePickerOwnedCodes,
    )
  }, [hostTemplate, hostRequirement, isInstalled, hostValues, structurePickerOwnedCodes])

  // Continue gate: require warranty resolved + all required host attributes.
  const canContinue = useMemo(() => {
    if (!state.selectedProduct) return false
    if (showProductPanel && !warrantyResolved) return false
    return missingHostAttrs.length === 0
  }, [state.selectedProduct, showProductPanel, warrantyResolved, missingHostAttrs])

  // Surface the gate through the wizard validation context so the
  // always-enabled Continue button intercepts the click, then show the
  // missing product or host fields inline.
  useRegisterValidator('host_product:complete', canContinue)
  const { attempted } = useWizardValidation()
  const productError = attempted && !state.selectedProduct
    ? 'Select a product to continue'
    : null
  const { t } = useTranslation()
  const hostAttributeErrors = useMemo(
    () =>
      attempted
        ? requiredHostAttributeErrors(missingHostAttrs, (label) =>
            t('validation.fieldRequired', '{{field}} is required', { field: label }),
          )
        : {},
    [attempted, missingHostAttrs, t],
  )

  if (!hostTemplate) return null

  return (
    <div className="flex h-full flex-col">
      <RegistrationStepGrid>
        {/* 1. Product (top-left) - only when this segment owns product selection */}
        {showProductPanel && (
          <ProductPanel
            state={state}
            update={update}
            detectedCategory={detectedCategory}
            metadata={metadata}
            flatProductSelection={flatProductSelection}
            requiredError={productError}
          />
        )}

        {/* 2. Vehicle / Host (top-right) - appears after product + warranty resolved */}
        {showVehicle && (
          <div className="panel-arrive">
            <HostPanel
              hostTemplate={hostTemplate}
              hostRequirement={hostRequirement}
              hostValues={hostValues}
              isInstalled={isInstalled}
              onValuesChange={handleHostValuesChange}
              onIsInstalledChange={handleIsInstalledChange}
              onCategoryDetected={setDetectedCategory}
              attempted={attempted}
              excludeCodeNames={structurePickerOwnedCodes}
              attributeErrors={hostAttributeErrors}
            />
          </div>
        )}

        {/* Structure picker - only renders when registering against a
            property that already exists and the tenant has a structure
            entity. Lets the operator attach this registration to one of
            the property's existing structures, or add a new one inline. */}
        {showVehicle &&
          !!state.selectedExistingHostUuid &&
          state.selectedExistingHostUuid !== 'new' && (
            <div className="panel-arrive">
              <StructurePickerPanel
                state={state}
                update={update}
                onActivationChange={handleStructurePickerActivation}
              />
            </div>
          )}

        {/* 3+ Additional detail fields as individual cards */}
        {showDetails && metadata && (
          <div className="panel-arrive contents">
            <AdditionalDetailCards state={state} update={update} metadata={metadata} />
          </div>
        )}

        {/* Mailing address - rendered alongside the property so the
            installation address is still in view when the user
            toggles "Same as installation". */}
        {showVehicle && mailingVisible && (
          <div className="panel-arrive">
            <MailingAddressPanel
              state={state}
              update={update}
              label={mailingLabel}
              required={mailingRequired}
              hostAttributes={hostTemplate.attributes}
            />
          </div>
        )}

        {extraPanels}
      </RegistrationStepGrid>

      <RegistrationStepActions
        onContinue={next}
        onBack={back}
        continueHint={continueHint}
        className="panel-arrive mt-4"
      />
    </div>
  )
}
